import React, { useState, useEffect } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import { PAYMENT_MODES } from '../../constants/visitMetadata';
import { updateVisitBilling } from '../../services/api/visitApi';
import { listReceiptsForVisit, sumCollectedRupees } from '../../services/api/receiptsApi';

/**
 * BillingCard — Optional billing details card shown in VisitActionsPanel.
 *
 * Records consultation fee and (for new patients) registration fee.
 * Fees are pre-filled from org settings if available, fully editable.
 * Accessible to RECEPTIONIST and ADMIN.
 *
 * R5 (owner) — REVISED: read-only is driven by the `readOnly` prop, set by
 * the CALLER (VisitActionsPanel, itself only told `readOnly` by ManageVisitPage
 * when the viewer is a RECEPTIONIST). BillingCard does NOT re-derive role
 * itself — a role-based check here would also flip ReceptionistDashboard's
 * card (a receptionist's OWN mount, which must stay fully editable). When
 * `readOnly`, fees + total render as plain text, never inputs, no Save
 * control, no `updateVisitBilling` call whatsoever.
 *
 * POST/PUT /api/organisation/visits/:visitId/billing
 *
 * W1-T3 fix — the "Total" tile used to sum only the two editable fee
 * inputs, so a visit whose money was collected via procedure receipts
 * (OTC/nurse visits, or an OPD visit with a procedure on top of the
 * consult) showed an under-reported or ₹0 total. It now reads the
 * canonical `sumCollectedRupees(receipts)` — the SAME total the receipts
 * modal and token print — falling back to the fee inputs only when the
 * visit has no receipt rows at all (pre-dual-write legacy visits, or the
 * receipts fetch failing). Receipts always win when non-empty so a
 * partially-billed visit never double-counts.
 *
 * VisitActionsPanel only mounts ONE instance of this card at a time (the
 * single expanded visit row, and only once its Billing tab is opened —
 * see ManageVisitPage/DoctorQueueView/NurseTaskCards/ReceptionistDashboard/
 * AdminSummary, all of which gate the panel behind `expandedVisitId`/
 * `activeTab`). The receipts fetch below therefore fires once per
 * billing-tab-open for that one visit, not per list row.
 */
export default function BillingCard({ visit, orgFeeSettings, readOnly = false, onClose }) {
  const { isDark } = useTheme();
  const isReadOnly = readOnly;
  const [consultationFee, setConsultationFee] = useState('');
  const [registrationFee, setRegistrationFee] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [receipts, setReceipts] = useState([]);

  // Canonical collected total — fetched once per visit (stable primitive
  // dep). Fetch failure or a genuinely empty receipts list both resolve to
  // `[]`, which the fallback below turns into the fee-input total instead
  // of a crash or NaN.
  useEffect(() => {
    let cancelled = false;
    setReceipts([]);
    listReceiptsForVisit(visit.visit_id).then((r) => {
      if (cancelled) return;
      setReceipts(r?.success ? r.data : []);
    });
    return () => { cancelled = true; };
  }, [visit.visit_id]);

  // Pre-fill from existing visit data or org settings
  useEffect(() => {
    if (visit.consultation_fee != null) {
      setConsultationFee(String(visit.consultation_fee));
    } else if (orgFeeSettings?.doctorFees?.[visit.doctor_member_id] != null) {
      setConsultationFee(String(orgFeeSettings.doctorFees[visit.doctor_member_id]));
    }

    if (visit.registration_fee != null) {
      setRegistrationFee(String(visit.registration_fee));
    } else if (visit.is_new_patient && orgFeeSettings?.registrationFeeEnabled && orgFeeSettings?.registrationFee != null) {
      setRegistrationFee(String(orgFeeSettings.registrationFee));
    }
  }, [visit, orgFeeSettings]);

  const showRegFee = visit.is_new_patient && orgFeeSettings?.registrationFeeEnabled;

  const consultAmt = parseFloat(consultationFee) || 0;
  const regAmt = parseFloat(registrationFee) || 0;
  const feesTotal = consultAmt + regAmt;

  // Receipts win whenever the visit has any (procedures, consult, reg, or
  // any mix) — that's the actual money collected. Only fall back to the
  // editable fee inputs when receipts is empty (legacy receipt-less visit,
  // or the fetch hasn't resolved/failed yet).
  const total = receipts.length > 0 ? sumCollectedRupees(receipts) : feesTotal;

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await updateVisitBilling(visit.visit_id, {
        consultationFee: consultationFee !== '' ? consultationFee : undefined,
        registrationFee: registrationFee !== '' ? registrationFee : undefined,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save billing');
    } finally {
      setSaving(false);
    }
  };

  const paymentLabel = PAYMENT_MODES.find((p) => p.id === visit.payment_mode);

  const inputStyle = {
    width: '100%',
    padding: '0.5rem 0.625rem',
    borderRadius: '0.5rem',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)'}`,
    background: isDark ? 'rgba(255,255,255,0.05)' : '#f9fafb',
    color: isDark ? '#f5f5f7' : '#111827',
    fontSize: '0.875rem',
  };

  const labelStyle = {
    fontSize: '0.75rem',
    fontWeight: 500,
    color: isDark ? '#9ca3af' : '#6b7280',
    marginBottom: '0.3rem',
    display: 'block',
  };

  // R5 — the receptionist's read-only value: same solid surface as the
  // editable input, just not a form control (a plain <div>, not a
  // `disabled` input — hidden-vs-disabled: this is a genuinely read-only
  // VIEW, not a temporarily-locked field).
  const readOnlyValueStyle = {
    ...inputStyle,
    fontWeight: 600,
  };

  return (
    <div style={{ padding: '1rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1rem' }}>💰</span>
          <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: isDark ? '#f5f5f7' : '#111827' }}>
            Billing
          </h3>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: isDark ? '#6b7280' : '#9ca3af' }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Payment Mode (read-only — set at registration) */}
      {paymentLabel && (
        <div style={{ marginBottom: '0.875rem' }}>
          <span style={labelStyle}>Payment Mode</span>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
            padding: '0.3125rem 0.75rem', borderRadius: '9999px',
            background: isDark ? 'rgba(20,184,166,0.12)' : 'rgba(20,184,166,0.08)',
            color: '#0d9488', fontSize: '0.8125rem', fontWeight: 600,
          }}>
            {paymentLabel.icon} {paymentLabel.label}
          </div>
        </div>
      )}

      {/* Consultation Fee */}
      <div style={{ marginBottom: '0.75rem' }}>
        <label style={labelStyle}>Consultation Fee (₹)</label>
        {isReadOnly ? (
          <div style={readOnlyValueStyle}>₹{consultationFee || '0'}</div>
        ) : (
          <input
            type="number"
            min="0"
            step="1"
            placeholder="e.g. 500"
            value={consultationFee}
            onChange={(e) => setConsultationFee(e.target.value)}
            style={inputStyle}
          />
        )}
      </div>

      {/* Registration Fee — only for new patients if admin enabled */}
      {showRegFee && (
        <div style={{ marginBottom: '0.75rem' }}>
          <label style={labelStyle}>
            Registration Fee (₹)
            <span style={{
              marginLeft: '0.375rem', fontSize: '0.6875rem', fontWeight: 500,
              background: isDark ? 'rgba(20,184,166,0.12)' : 'rgba(20,184,166,0.08)',
              color: '#14b8a6', padding: '0.125rem 0.375rem', borderRadius: '9999px',
            }}>New Patient</span>
          </label>
          {isReadOnly ? (
            <div style={readOnlyValueStyle}>₹{registrationFee || '0'}</div>
          ) : (
            <input
              type="number"
              min="0"
              step="1"
              placeholder="e.g. 100"
              value={registrationFee}
              onChange={(e) => setRegistrationFee(e.target.value)}
              style={inputStyle}
            />
          )}
        </div>
      )}

      {/* Total — shown whenever there's a fee input OR a collected receipt
          (procedure-only OTC/nurse visits have no fee input at all). */}
      {(consultationFee || (showRegFee && registrationFee) || total > 0) && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '0.625rem 0.75rem', borderRadius: '0.5rem', marginBottom: '0.875rem',
          background: isDark ? 'rgba(255,255,255,0.04)' : '#f3f4f6',
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
        }}>
          <span style={{ fontSize: '0.8125rem', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>Total</span>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: isDark ? '#f5f5f7' : '#111827' }}>
            ₹{total.toLocaleString('en-IN')}
          </span>
        </div>
      )}

      {!isReadOnly && error && (
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.8125rem', color: '#ef4444' }}>{error}</p>
      )}

      {/* R5 — no Save control for the receptionist's read-only view; the
          absence of the button is the cue, no new copy needed. */}
      {!isReadOnly && (
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            width: '100%', padding: '0.625rem', borderRadius: '0.5rem', border: 'none',
            background: saved ? '#22c55e' : 'linear-gradient(135deg, #0d9488, #14b8a6)',
            color: '#fff', fontWeight: 600, fontSize: '0.875rem',
            cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
            transition: 'background 0.2s',
          }}
        >
          {saving ? 'Saving...' : saved ? '✅ Saved' : 'Save Billing'}
        </button>
      )}
    </div>
  );
}
